import { LoadingProgress } from '@issuemanager/apps/client/components';
import {
  useAllQueries,
  useMonitorManageIssueFormLoad,
} from '@issuemanager/apps/client/hooks';
import { useIssueTemplateByIssueTypeAndIssueSubtype } from '@issuemanager/client/features/issue-templates';
import { useSaveIssue } from '@issuemanager/client/features/issues';
import { useSLAsByIssueTypeAndIssueSubtype } from '@issuemanager/client/features/slas';
import { IssueStatusEnum } from '@issuemanager/constants';
import { Stack, useEventCallback } from '@mui/material';
import { useSnackbar } from 'notistack';
import React, { useEffect, useMemo, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  IssueApprovePanel,
  IssueDeclinePanel,
} from '../components/panels/issue';
import { useHeaderButtons } from '../hooks/use-header-buttons';
import { useIssueStatusInfo } from '../hooks/use-issue-status-info';
import {
  ActionField,
  useManageIssueForm,
} from '../hooks/use-manage-issue-form';
import useResetFormData from '../hooks/use-reset-form-data';
import { useIssueOutletContext } from '../hooks/useIssueOutletContext';
import { ActionFields } from './action-fields';
import { Documents } from './documents';
import { IssueFields } from './issue-fields';

export default function ManageIssue(): React.JSX.Element {
  const { t } = useTranslation(['common', 'issue', 'action']);
  const { code, issue, userRoles: issueUserRoles } = useIssueOutletContext();
  const monitorManageIssueLoad = useMonitorManageIssueFormLoad();
  const { enqueueSnackbar } = useSnackbar();

  const { isEditable, isInitiated } = useIssueStatusInfo({
    status: issue.data?.status,
    actionProgress: issue.data?.actionProgress,
    approvalStatus: issue.data?.approvalStatus,
  });

  const [selectedPanel, setSelectedPanel] = useState<
    'decline' | 'approve' | null
  >(null);

  const [isEditing, setIsEditing] = useState(false);

  const SLAs = useSLAsByIssueTypeAndIssueSubtype({
    issueType: issue.data?.issueType?.name ?? '',
    issueSubtype: issue.data?.issueSubtype?.id,
  });

  const issueTemplate = useIssueTemplateByIssueTypeAndIssueSubtype({
    issueTypeId: issue.data?.issueType?.id ?? '',
    issueSubtypeId: issue.data?.issueSubtype?.id,
    enabled:
      issue.data?.status === IssueStatusEnum.OPEN &&
      issue.data &&
      !issue.data.selectedTemplate,
  });

  const isIssueManager = Boolean(issueUserRoles.data?.isManager);

  const messageKey = useMemo(() => {
    const isInProgress = issue.data?.status === IssueStatusEnum.IN_PROGRESS;

    if (isInProgress) {
      if (isEditing) {
        return 'issue:manage.saveAndInitiate.success';
      }
      return 'issue:manage.initiate.success';
    }

    if (isEditing) {
      return 'issue:manage.saveAndAssign.success';
    }

    return 'issue:manage.assign.success';
  }, [issue.dataUpdatedAt, isEditing]);

  const handleSubmitSuccess = () => {
    enqueueSnackbar(t(messageKey), {
      variant: 'success',
    });

    if (isEditing) {
      setIsEditing(false);
    }
  };

  const {
    manageIssueForm,
    assignIssueIsLoading,
    initiateWorkflowIsLoading,
    manageIssueHandleSave,
    manageIssueHandleSubmit,
    issueFields,
    actionFields,
    onAddAction,
    watched: { actions, executionType },
  } = useManageIssueForm({
    SLAs: SLAs.data,
    issueTemplate: issueTemplate.data,
    readOnly: !isEditing || (isEditing && isInitiated && !isIssueManager),
    onSubmitSuccess: handleSubmitSuccess,
  });

  const handleSaveSuccess = () => {
    enqueueSnackbar(t('issue:manage.save.success'), {
      variant: 'success',
    });
    setIsEditing(false);
  };

  const saveIssue = useSaveIssue({ code, onSuccess: handleSaveSuccess });

  const handleSave = useEventCallback(() => {
    const issueStatus = issue.data?.status;
    if (!issueStatus) return;

    manageIssueHandleSave(saveIssue);
  });

  useAllQueries({
    queries: [issue],
    onSuccess: () => {
      monitorManageIssueLoad.stop();
    },
    onError: () => {
      monitorManageIssueLoad.error();
    },
  });

  const isLoading =
    issue.isFetching || SLAs.isFetching || issueTemplate.isFetching;

  useHeaderButtons({
    manageIssueForm,
    assignIssueIsLoading,
    initiateWorkflowIsLoading,
    manageIssueHandleSubmit,
    isEditing,
    setIsEditing,
    isLoading,
    activateDeclinePanel: () => setSelectedPanel('decline'),
    activateApprovePanel: () => setSelectedPanel('approve'),
    handleSave,
    saveIssue,
  });

  const { resetForm } = useResetFormData({
    manageIssueForm,
  });

  useEffect(() => {
    resetForm();
  }, [resetForm, issue.dataUpdatedAt]);

  const handleDragEnd = useEventCallback((actions: ActionField[]) => {
    manageIssueForm.setValue('actions', actions);
    manageIssueForm.trigger('actions');
  });

  const actionValues = useMemo(() => {
    if (issue.data?.actions.length) {
      return issue.data?.actions;
    }
    return [];
  }, [issue.dataUpdatedAt]);

  return (
    <Stack data-testid='manage-issue' spacing={3} p={4}>
      {isLoading ? (
        <LoadingProgress height={600} />
      ) : (
        <FormProvider {...manageIssueForm}>
          <IssueFields {...issueFields} />

          <Documents
            form={manageIssueForm}
            isEditing={isEditing}
            attachments={issue.data?.attachments ?? []}
            isIssueEditable={isEditable}
            onEdit={() => setIsEditing(true)}
          />

          <ActionFields
            issueFields={issueFields}
            actionFields={actionFields}
            actionsFormValues={actions}
            actionValues={actionValues}
            onAddAction={onAddAction}
            isEditing={isEditing}
            onEdit={() => setIsEditing(true)}
            executionType={executionType}
            isEditable={isEditable}
            isWorkflowBlocked={issue.data?.isWorkflowBlocked}
            onDragEnd={handleDragEnd}
          />
          <IssueDeclinePanel
            actions={issue.data?.actions || []}
            code={code}
            isOpen={selectedPanel === 'decline'}
            onClose={() => setSelectedPanel(null)}
          />
          <IssueApprovePanel
            code={code}
            isOpen={selectedPanel === 'approve'}
            onClose={() => setSelectedPanel(null)}
          />
        </FormProvider>
      )}
    </Stack>
  );
}
